import type { CustomRestaurantPalette, RestaurantPaletteKey } from '@/types/commerce';

export interface RestaurantPaletteTokens {
  key: RestaurantPaletteKey;
  name: string;
  subtitle: string;
  accentDark: string;
  accentLight: string;
  bgDark: string;
  cardDark: string;
  cardLowDark: string;
  borderDark: string;
  inkDark: string;
  inkMutedDark: string;

  bgLight: string;
  cardLight: string;
  cardLowLight: string;
  borderLight: string;
  inkLight: string;
  inkMutedLight: string;
}

export const RESTAURANT_PRESET_PALETTES: Record<
  Exclude<RestaurantPaletteKey, 'custom'>,
  RestaurantPaletteTokens
> = {
  gilded_ivory: {
    key: 'gilded_ivory',
    name: 'Gilded Ivory & Midnight',
    subtitle: 'Or raffiné, charcoal profond & ivoire chaleureux (Fine dining & Steakhouses)',
    accentDark: '#f2ca50',
    accentLight: '#735c00',
    bgDark: '#131313',
    cardDark: '#20201f',
    cardLowDark: '#1b1b1b',
    borderDark: 'rgba(153, 144, 124, 0.3)',
    inkDark: '#e5e2e1',
    inkMutedDark: '#d0c5af',

    bgLight: '#f8fafc',
    cardLight: '#ffffff',
    cardLowLight: '#f1f5f9',
    borderLight: '#e2e8f0',
    inkLight: '#0f172a',
    inkMutedLight: '#64748b',
  },
  emerald_bistro: {
    key: 'emerald_bistro',
    name: 'Émeraude & Sauge Organique',
    subtitle: 'Émeraude profond, sauge & blanc menthe (Bistros bio, cafés & healthy)',
    accentDark: '#52b788',
    accentLight: '#2d6a4f',
    bgDark: '#0b1d17',
    cardDark: '#162e24',
    cardLowDark: '#11241c',
    borderDark: 'rgba(82, 183, 136, 0.3)',
    inkDark: '#e8f5e9',
    inkMutedDark: '#a3b18a',

    bgLight: '#f8fafc',
    cardLight: '#ffffff',
    cardLowLight: '#f1f5f9',
    borderLight: '#e2e8f0',
    inkLight: '#0f172a',
    inkMutedLight: '#64748b',
  },
  bordeaux_rosewood: {
    key: 'bordeaux_rosewood',
    name: 'Bordeaux & Rose Boisé',
    subtitle: 'Bordeaux carmin, rose boisé & sable chaud (Bars à vin, trattorias & grills)',
    accentDark: '#ff4d6d',
    accentLight: '#b7094c',
    bgDark: '#1c0b11',
    cardDark: '#29121b',
    cardLowDark: '#220e16',
    borderDark: 'rgba(255, 77, 109, 0.3)',
    inkDark: '#fff0f3',
    inkMutedDark: '#ffb3c1',

    bgLight: '#f8fafc',
    cardLight: '#ffffff',
    cardLowLight: '#f1f5f9',
    borderLight: '#e2e8f0',
    inkLight: '#0f172a',
    inkMutedLight: '#64748b',
  },
  midnight_cobalt: {
    key: 'midnight_cobalt',
    name: 'Midnight Cobalt & Cuivre',
    subtitle: 'Cobalt nuit, cuivre ambré & acier glacier (Lounges, rooftops & bars à cocktails)',
    accentDark: '#fbbf24',
    accentLight: '#d97706',
    bgDark: '#0f172a',
    cardDark: '#1e293b',
    cardLowDark: '#172033',
    borderDark: 'rgba(251, 191, 36, 0.3)',
    inkDark: '#f8fafc',
    inkMutedDark: '#cbd5e1',

    bgLight: '#f8fafc',
    cardLight: '#ffffff',
    cardLowLight: '#f1f5f9',
    borderLight: '#e2e8f0',
    inkLight: '#0f172a',
    inkMutedLight: '#64748b',
  },
};

export function resolveRestaurantPalette(
  _paletteKey?: RestaurantPaletteKey | null,
  _customPalette?: CustomRestaurantPalette | null,
  _mode?: 'dark' | 'light',
  accentColor?: string | null,
): Record<string, string> {
  const tokens = RESTAURANT_PRESET_PALETTES.gilded_ivory;
  const storeAccent = accentColor || tokens.accentLight;

  return {
    '--store-accent': storeAccent,
    '--store-accent-foreground': '#ffffff',
    '--store-bg': tokens.bgLight,
    '--store-surface': tokens.cardLight,
    '--store-surface-low': tokens.cardLowLight,
    '--store-border': tokens.borderLight,
    '--store-ink': tokens.inkLight,
    '--store-ink-muted': tokens.inkMutedLight,
  };
}

export function resolvePageRestaurantPalette(
  page:
    | {
        accent_color?: string | null;
        restaurant_palette?: RestaurantPaletteKey | null;
        custom_restaurant_palette?: CustomRestaurantPalette | null;
        settings?: Record<string, unknown> | null;
      }
    | null
    | undefined,
  _mode?: 'dark' | 'light',
): Record<string, string> {
  const settingsObj = page?.settings as Record<string, unknown> | null | undefined;
  const accentColor = page?.accent_color ?? (settingsObj?.accent_color as string | undefined);
  return resolveRestaurantPalette('gilded_ivory', null, 'light', accentColor);
}
